import {
  Divider,
  Flex,
  HStack,
  useDisclosure,
  useToast,
  Stack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useLegacyIframeResizeMessage from "../../hooks/useLegacyIframeResizeMessage";
import { ReactComponent as LeftIcon } from "../../assets/chevron-left.svg";
import { ReactComponent as CalendarIcon } from "../../assets/calendar.svg";
import { ReactComponent as AlertTriangle } from "../../assets/alert-triangle.svg";
import { ReactComponent as RightIcon } from "../../assets/chevron-right.svg";
import styles from "../confirm.css";
import SidePanel from "../../Components/SidePanel/SidePanel";
import Calendar from "../../Components/Calendar/Calendar";
import LargeContainer from "../../Components/Container/LargeContainer";
import SmallContainer from "../../Components/Container/SmallContainer";
import Heading from "../../Components/Text/Heading";
import SubHeading from "../../Components/Text/SubHeading";
import Form from "../../Components/Forms/Form";
import Slots from "../../Components/Slots/Slots";
import CustomButton from "../../Components/Button/CustomButton";
import BookingDateForm from "../../Components/Forms/BookingDateForm";
import { getDailyRecords } from "../../utils/DailyRecords";
import {
  deleteBooking,
  searchExistingBooking,
  getMonthlyData,
  searchForBooking,
  updateBookingDate,
  getEffectiveBookingWindow,
} from "../../bookingapi/booking.operations";
import LoadingScreen from "../../Components/LoadingScreen/LoadingScreen";
import useFormConfig from "../../hooks/useFormConfig";


const dayLabels = [
    { "name": "mon", "abbreviation": "mon" },
    { "name": "tue", "abbreviation": "tue" },
    { "name": "wed", "abbreviation": "wed" },
    { "name": "thu", "abbreviation": "thu" },
    { "name": "fri", "abbreviation": "fri" },
    { "name": "sat", "abbreviation": "sat" },
    { "name": "sun", "abbreviation": "sun" }
  ]

export default function UpdateBookingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  const { formConfig } = useFormConfig();

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const [bookingId, setBookingId] = useState();
  const [isAmend, setIsAmend] = useState(false);
  const [calendarDate, setCalendarDate] = useState(tomorrow);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentBookingDate, setCurrentBookingDate] = useState();
  const [bookingNotFound, setBookingNotFound] = useState(false);
  const [monthlyData, setMonthlyData] = useState({});
  const [dailyData, setDailyData] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState({});
  const [originalBooking, setOriginalBooking] = useState();
  const [loading, setLoading] = useState(false);
  const [updateToken, setUpdateToken] = useState(token);
  const [loader, setLoader] = useState(true);
  const [bookingWindow, setBookingWindow] = useState(null);

  const toast = useToast();
  const year = calendarDate.getFullYear();
  const month = calendarDate.getMonth() + 1;
  useLegacyIframeResizeMessage([isAmend]);

  const { emailAddress, phoneNumber, bookingWindow: navBookingWindow } = location?.state || {};

  // Use booking window from navigation state if available
  useEffect(() => {
    if (navBookingWindow) setBookingWindow(navBookingWindow);
  }, [navBookingWindow]);

  function throwToastError(title, description = "") {
    toast({
      title,
      description,
      status: "error",
      duration: 3000,
      isClosable: true,
    });
  }

  useEffect(() => {
    const searchBooking = async () => {
      try {
        let tokenToUse = updateToken;
        if (!tokenToUse) {
          const existingBooking = await searchExistingBooking(
            phoneNumber,
            emailAddress
          );
          tokenToUse = existingBooking;
          setUpdateToken(tokenToUse);
          navigate(`/update-booking?token=${existingBooking.data}`);
        }
        const booking = await searchForBooking(tokenToUse);

        if (!booking) return setBookingNotFound(true);
        setBookingNotFound(false);
        setOriginalBooking(booking.data);

        const timestamp = booking?.data.dayId?.date; // timestamp value in milliseconds
        const formattedDate = new Date(timestamp);
        setCurrentBookingDate(formattedDate);

        setSelectedSlot(booking?.data.timeSlotId);
        setBookingId(booking?.data.bookingId);
        updateMonthlyData();
      } catch (error) {
        console.log(error.message);
        setBookingNotFound(true);
      }
    };
    searchBooking();
  }, [token, navigate, phoneNumber, emailAddress, updateToken]);

  useEffect(() => {
    if (selectedDate) {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1; // Add 1 to get the month number starting from 1 instead of 0
      const day = selectedDate.getDate();

      getDailyRecords(day, month, year, setDailyData);
    }
  }, [selectedDate]);

  const updateBooking = async (event) => {
    setLoading(true);
    if (
      !originalBooking.customerFirstName ||
      originalBooking.customerFirstName === ""
    ) {
      throwToastError("Invalid First Name.");
      return;
    }
    if (
      !originalBooking.customerLastName ||
      originalBooking.customerLastName === ""
    ) {
      throwToastError("Invalid Last Name.");
      return;
    }
    if (!bookingId || bookingId === "" || bookingId < 0) {
      throwToastError("Invalid Booking ID.");
      return;
    }
    if (!selectedDate || selectedDate === "") {
      throwToastError("Please select a date");
    }

    try {
      let body = {
        timeSlotId: selectedSlot.timeSlotId,
        year: selectedDate.getFullYear(),
        month: selectedDate.getMonth() + 1,
        day: selectedDate.getDate(),
        token: token,
      };

      const response = await updateBookingDate(bookingId, body);

      setLoading(false);
      if (response.data === "Slot not found") {
        throwToastError("Booking already found");
        return;
      }

      if (response.data === "Booking already found") {
        throwToastError("Booking already found");
        return;
      }

      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1; // Add 1 to get the month number starting from 1 instead of 0
      const day = selectedDate.getDate();

      getMonthlyData(year, month);
      getDailyRecords(day, month, year, setDailyData);

      toast({
        title: "Booking Updated.",
        description: "Your Booking has been updated",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setSelectedSlot("");
      setBookingId("");
      navigate("/booking-confirmed", {
        state: { selectedSlot, selectedDate },
      });
      setIsAmend(false);
    } catch (error) {
      throwToastError("Something went wrong", error?.message || "");
    }
  };

  const handleDelete = async (event) => {
    if (!bookingId || bookingId === "" || bookingId < 0 || !token) {
      throwToastError("Invalid Booking ID.");
      return;
    }

    try {
      await deleteBooking(bookingId, token);
      // TODO: check for success
      onClose();
      setBookingNotFound(true);
      navigate("/", { replace: true });
      toast({
        title: "Booking Deleted.",
        description: "Your Booking has been deleted",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      setSelectedSlot("");
      setBookingId("");
    } catch (error) {
      throwToastError("Something went wrong", error?.message || "");
    }
  };

  function getAvailableSlotsForDay(day) {
    // Find the corresponding day data in the monthlyData array
    const dayData = monthlyData?.find((d) => d.date.split("-")[2] === day);
    // If dayData exists, return the availableSlots, otherwise return 0
    const slots = dayData ? dayData?.availableSlots - dayData?.bookedSlots : 0;
    // given customer visibile app, mask possible negative slots.
    return slots > 0 ? slots : 0;
  }

  const { isOpen, onOpen, onClose } = useDisclosure();

  async function updateMonthlyData(newYear = year, newMonth = month) {
    try {
      setLoader(true);
      const data = await getMonthlyData(newYear, newMonth);
      setMonthlyData(data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoader(false);
    }
  }


  function subtractMonth() {
    const newDate = new Date(calendarDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCalendarDate(newDate);
    updateMonthlyData(newDate.getFullYear(), newDate.getMonth() + 1);
  }
  function addMonth() {
    const newDate = new Date(calendarDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCalendarDate(newDate);
    updateMonthlyData(newDate.getFullYear(), newDate.getMonth() + 1);
  }

  function isDateOpen(day) {
    const dayData = monthlyData?.find((d) => d.date.split("-")[2] == day);
    if (!dayData?.open) return false;
    if (dayData.availableSlots <= 0) return false;

    // Apply user-specific booking window constraints (may differ from server's generic window)
    if (bookingWindow) {
      const date = new Date(year, month - 1, day);
      date.setHours(0, 0, 0, 0);
      if (bookingWindow.start) {
        const start = new Date(bookingWindow.start);
        start.setHours(0, 0, 0, 0);
        if (date < start) return false;
      }
      if (bookingWindow.end) {
        const end = new Date(bookingWindow.end);
        end.setHours(23, 59, 59, 999);
        if (date > end) return false;
      }
    }

    return true;
  }

  function renderDays() {
    // Get today's date
    let today = new Date();
    // Make sure to set the time to midnight for a fair comparison
    today.setHours(0, 0, 0, 0);

    return (
      <>
        {loader ? (
          <LoadingScreen
            loadingText={"Hold tight"}
            loadingSubText={"It will just take a few moments"}
          />
        ) : (
          <Calendar
            isDateOpen={isDateOpen}
            getAvailableSlotsForDay={getAvailableSlotsForDay}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            calendarDate={calendarDate}
          />
        )}
      </>
    );
  }

  // NOT FOUND PAGE
  if (bookingNotFound) {
    return (
      <LargeContainer h="100vh">
        <Stack bg="white" p="32px" rounded={5} boxShadow="lg">
          <Stack spacing="20x" className={styles.referrerBookingConfirmed}>
            <div className={styles.inputFieldsearch}></div>

            <div className={styles.iconscheckCircleParent}>
              <AlertTriangle />
              <div className={styles.bookingConfirmedParent}>
                <b className={styles.bookingConfirmed}>
                  Unable to find your booking
                </b>
                <div style={{ width: "50vw" }}></div>
              </div>
            </div>
          </Stack>
        </Stack>
      </LargeContainer>
    );
  }

  const formattedDate = ` ${calendarDate.toLocaleString("default", {
    month: "long",
  })} ${calendarDate.getFullYear()}`;
  
  return (
    <LargeContainer padding="5px" minH={"100vh"}>
      <SmallContainer
        px={{ base: "2%" }}
        maxW="1200px"
        margin="0 auto"
      >
        <SmallContainer
          py={"20px"}
          justifyContent="space-between"
          flexDirection={{ base: "column", md: "row" }}
        >
          {bookingId && bookingId !== "" && (
            <>
              {isAmend ? (
                <>
                  <SmallContainer
                    width={{ base: "100%", md: "49.14%" }}
                    backgroundColor={"#FFFFFF"}
                    alignItems={"center"}
                  >
                    <Form
                      formConfig={formConfig}
                      formData={originalBooking}
                      disabled
                      displayedUIForm
                    ></Form>

                    <Flex width={"92.92%"} justifyContent={"center"}>
                      <Flex width={"100%"} flexDirection={"column"}>
                        <Flex width={"100%"} minheight={"80px"}>
                          <Flex marginTop={"27px"}>
                            <LeftIcon
                              style={{
                                cursor: "pointer",
                              }}
                              onClick={subtractMonth}
                            />
                            &nbsp;
                            <RightIcon
                              style={{
                                cursor: "pointer",
                              }}
                              onClick={addMonth}
                            />
                          </Flex>
                          <Heading headingLabel={formattedDate}></Heading>
                        </Flex>
                        <HStack spacing="1.26%" marginTop={"16px"}>
                          {dayLabels.map((day, index) => (
                            <SubHeading
                              key={index}
                              textAlign={"center"}
                              w="13.2%"
                              h="16px"
                              subHeadingLabel={day.abbreviation}
                            ></SubHeading>
                          ))}
                        </HStack>
                        <Flex marginTop={"8px"} marginBottom="16px">
                          {renderDays()}
                        </Flex>
                      </Flex>
                    </Flex>
                  </SmallContainer>

                  <SmallContainer
                    mt={{ base: "20px", md: "0px" }}
                    width={{ base: "100%", md: "49.14%" }}
                    backgroundColor={"#FFFFFF"}
                    alignItems={"center"}
                  >
                    <SmallContainer width={"92.92%"}>
                      <Heading headingLabel="Choose your time slot"></Heading>
                      <Flex width={"23.81%"} marginTop={"10px"}>
                        <SubHeading subHeadingLabel="Available Slots *" />
                      </Flex>
                      <Slots
                        dailyData={dailyData}
                        selectedSlot={selectedSlot}
                        setSelectedSlot={setSelectedSlot}
                        selectedDate={selectedDate}
                      />
                    </SmallContainer>
                    <SmallContainer minHeight={"104px"}>
                      <Divider />

                      {selectedDate && (
                        <>
                          <CustomButton
                            width="80%"
                            marginTop={"16px"}
                            onClick={updateBooking}
                            _hover={{
                              background: "blue.800",
                            }}
                            buttonLabel="Amend Booking"
                            isDisabled={loading}
                            isLoading={loading}
                          />

                          <CustomButton
                            onClick={onOpen}
                            marginTop={"16px"}
                            backgroundColor={"#00E0FF"}
                            buttonLabel="Delete Booking"
                            marginBottom={"16px"}
                            width="80%"
                          />
                        </>
                      )}
                    </SmallContainer>
                  </SmallContainer>
                </>
              ) : (
                <SmallContainer
                  justifyContent="space-between"
                  flexDirection={{ base: "column", md: "row" }}
                  backgroundColor={"#F5F5F5"}
                >
                  <SmallContainer
                    width={{ base: "100%", md: "49.14%" }}
                    backgroundColor={"#FFFFFF"}
                    alignItems={"center"}
                    padding={"20px"}
                    marginBottom={{ base: "20px", md: "0" }}
                  >
                    <HStack>
                      <CalendarIcon width={"40px"} height="40px" />
                      <Heading headingLabel="Your Current Booking"></Heading>
                    </HStack>
                    <Form
                      formConfig={formConfig}
                      updateBooking
                      formData={originalBooking}
                      disabled
                      displayedUIForm
                    />
                  </SmallContainer>
                  <SmallContainer
                    width={{ base: "100%", md: "49.14%" }}
                    backgroundColor={"#FFFFFF"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    padding={"20px"}
                  >
                    <SmallContainer width={"92.92%"}>
                      <BookingDateForm
                        currentBookingDate={currentBookingDate?.toLocaleDateString()}
                        selectedSlot={selectedSlot?.slotTime}
                      />
                      <SmallContainer marginTop={"20px"}>
                        <CustomButton
                          marginTop={"16px"}
                          onClick={async () => {
                            const userData = {
                              customerFirstName: originalBooking.customerFirstName,
                              customerLastName: originalBooking.customerLastName,
                              phoneNumber: originalBooking.phoneNumber,
                              emailAddress: originalBooking.emailAddress,
                              reference: originalBooking.reference,
                            };
                            const window = await getEffectiveBookingWindow(userData);
                            if (window) setBookingWindow(window);
                            setIsAmend(true);
                          }}
                          _hover={{
                            background: "blue.600",
                          }}
                          buttonLabel="Amend Booking"
                        />

                        <CustomButton
                          onClick={onOpen}
                          marginTop={"16px"}
                          backgroundColor={"#00E0FF"}
                          buttonLabel="Delete Booking"
                        />
                      </SmallContainer>
                    </SmallContainer>
                  </SmallContainer>
                </SmallContainer>
              )}
            </>
          )}
        </SmallContainer>
        <SidePanel
          isOpen={isOpen}
          onClose={onClose}
          title="Delete Booking"
          bodyText="Are you sure you want to delete this booking?"
          buttonLabel="Delete Booking"
          onClick={handleDelete}
          headingLabel="Delete Booking"
        />
      </SmallContainer>
    </LargeContainer>
  );
}
